import type { Reference } from '@medplum/fhirtypes'
import { formatDate, today } from '@seen/core/dates'
import { CompositionNote } from '@seen/core/models/compositionNote'
import { Desk } from '@seen/core/models/desk'
import { NoteLabel } from '@seen/core/models/noteLabel'
import { resolveId } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import { Badge, Item, ItemContent, ItemMedia, ItemTitle } from '@seen/ui'
import { IconDesk, IconSearch, IconTag } from '@tabler/icons-react'
import type { ColumnDef } from '@tanstack/react-table'
import { compact, orderBy } from 'lodash-es'
import { type JSX, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { create } from 'zustand'
import { combine, devtools, persist } from 'zustand/middleware'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { PractitionerDisplay } from '#src/components/display/PractitionerDisplay.tsx'
import type { Filter, FilterConfigMap } from '#src/components/filter/Filter.tsx'
import { FilterEditor } from '#src/components/filter/FilterEditor.tsx'
import { useFilteredData } from '#src/components/filter/useFilteredData.ts'
import { LabelColor } from '#src/components/LabelColor.tsx'
import { ParticipantTaskRowBadge } from '#src/components/ParticipantTaskRowBadge.tsx'
import { useLabelFilterOptions } from '#src/components/UseLabelFilterOptions.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useNavigateFromClickEvent } from '#src/hooks/useNavigateFromClickEvent.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import {
  extractFirstLineForNotesList,
  extractTextForNotesList,
} from '#src/pages/notes/notesListContentText.ts'
import { useNotesPageShardSearch } from '#src/pages/notes/useNotesPageShardSearch.ts'

const useNotesPageStore = create(
  devtools(
    persist(
      combine(
        {
          filters: [] as Filter[],
        },
        (set) => ({
          setFilters: (
            nextFilters: Filter[] | ((current: Filter[]) => Filter[])
          ) => {
            set((state) => ({
              filters:
                typeof nextFilters === 'function'
                  ? nextFilters(state.filters)
                  : nextFilters,
            }))
          },
        })
      ),
      { name: 'notes-page' }
    )
  )
)

function NoteTitleCell({
  content,
  subject,
  desks,
  originNode,
  labels,
}: {
  content: string
  subject: Reference | undefined
  desks: Desk[]
  originNode: string | undefined
  labels: NoteLabel[]
}): JSX.Element {
  const navigate = useNavigate()
  const title = useMemo(() => extractFirstLineForNotesList(content), [content])

  return (
    <div className="flex items-center gap-2">
      <span className="line-clamp-1 break-all">
        {title ? title : 'Untitled'}
      </span>
      {isOfType(subject, 'Patient') && (
        <ParticipantTaskRowBadge participant={subject} />
      )}
      {desks.map((desk) => (
        <Badge
          key={desk.id}
          variant="outline"
          className="shrink-0 cursor-pointer hover:bg-accent"
          onClick={(e) => {
            e.stopPropagation()
            void navigate(`/desks/${desk.id}#${originNode}`)
          }}
        >
          {desk.title}
        </Badge>
      ))}
      {labels.map((label) => (
        <Badge key={label.id} variant="outline" className="shrink-0 gap-1">
          <LabelColor color={label.color} />
          {label.name}
        </Badge>
      ))}
    </div>
  )
}

export function NotesPage(): JSX.Element {
  const navigateFromClick = useNavigateFromClickEvent()
  const currentPractitioner = useCurrentPractitioner()

  const noteListShards = useMemo(
    () => CompositionNote.globalNotesListShards(today()),
    []
  )

  const {
    data: unsortedNotes,
    isLoading: isLoadingNotes,
    isPending: isPendingNotes,
    isError: isShardNotesError,
    failedShardCount,
  } = useNotesPageShardSearch(noteListShards)

  const { data: desks, isLoading: isLoadingDesks } = useSearch(
    Desk,
    Desk.queryAll(currentPractitioner.reference)
  )

  const { data: noteLabels, isLoading: isLoadingLabels } = useSearch(
    NoteLabel,
    NoteLabel.baseSearchQuery()
  )

  const labelOptions = useLabelFilterOptions(noteLabels)

  const noteLabelsMap = useMemo(() => {
    const map = new Map<string, NoteLabel>()
    for (const label of noteLabels ?? []) {
      map.set(label.referenceString, label)
    }
    return map
  }, [noteLabels])

  const notes = useMemo(() => {
    const byId = new Map<string, CompositionNote>()
    for (const note of unsortedNotes) {
      if (!byId.has(note.id)) {
        byId.set(note.id, note)
      }
    }
    return orderBy(
      [...byId.values()],
      [(note) => note.period === undefined, (note) => note.period],
      ['asc', 'desc']
    )
  }, [unsortedNotes])

  const noteNodeToDesks = useMemo(() => {
    if (!desks) {
      return new Map<string, Desk[]>()
    }
    const map = new Map<string, Desk[]>()
    for (const desk of desks) {
      for (const node of desk.flowObject.nodes) {
        const existing = map.get(node.id) ?? []
        map.set(node.id, [...existing, desk])
      }
    }
    return map
  }, [desks])

  const noteContentTextMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const note of notes ?? []) {
      map.set(note.id, extractTextForNotesList(note.content))
    }
    return map
  }, [notes])

  const deskOptions = useMemo(
    () =>
      (desks ?? []).map((d) => ({
        label: d.title,
        value: d.id,
        keywords: [d.title],
      })),
    [desks]
  )

  const filterOptions = useMemo<FilterConfigMap<CompositionNote>>(
    () => ({
      content: {
        label: 'Content',
        icon: <IconSearch className="size-4" />,
        type: 'text',
        valueFn: (note) => noteContentTextMap.get(note.id) ?? '',
      },
      desk: {
        label: 'Desk',
        icon: <IconDesk className="size-4" />,
        type: 'multi-select-array',
        options: deskOptions,
        valueFn: (note) => {
          if (!note.originNode) {
            return []
          }
          const desksForNote = noteNodeToDesks.get(note.originNode) ?? []
          return desksForNote.map((d) => d.id)
        },
      },
      labels: {
        label: 'Labels',
        icon: <IconTag className="size-4" />,
        type: 'multi-select-array',
        options: labelOptions,
        valueFn: (note) => note.labels,
      },
    }),
    [deskOptions, labelOptions, noteContentTextMap, noteNodeToDesks]
  )

  const filters = useNotesPageStore((state) => state.filters)
  const setFilters = useNotesPageStore((state) => state.setFilters)
  const filteredNotes = useFilteredData(notes, filterOptions, filters)

  const columns: ColumnDef<CompositionNote>[] = useMemo(
    () => [
      {
        id: 'title',
        header: 'Title',
        accessorFn: (note) => note.content,
        cell: ({ row }) => {
          const note = row.original
          const desksForNote = note.originNode
            ? (noteNodeToDesks.get(note.originNode) ?? [])
            : []
          const labelsForNote = compact(
            note.labels.map((ref) => noteLabelsMap.get(ref))
          )
          return (
            <NoteTitleCell
              content={note.content}
              subject={note.subject}
              desks={desksForNote}
              originNode={note.originNode}
              labels={labelsForNote}
            />
          )
        },
      },
      {
        id: 'author',
        header: 'Author',
        accessorFn: (note) => note.owner?.reference,
        cell: ({ row }) => {
          const author = row.original.owner
          if (isOfType(author, 'Practitioner')) {
            return <PractitionerDisplay practitioner={author} />
          }
          return null
        },
      },
      {
        id: 'date',
        header: 'Date',
        accessorFn: (note) => note.period,
        cell: ({ row }) =>
          row.original.period ? formatDate(row.original.period) : null,
      },
    ],
    [noteLabelsMap, noteNodeToDesks]
  )

  if ((isLoadingNotes || isPendingNotes) && unsortedNotes.length === 0) {
    return <Loader />
  }

  if (isLoadingDesks || isLoadingLabels) {
    return <Loader />
  }

  return (
    <PageLayout title="Notes">
      <Item variant="outline" size="sm" className="mb-4">
        <ItemMedia>🚧</ItemMedia>
        <ItemContent>
          <ItemTitle>Under construction</ItemTitle>
        </ItemContent>
      </Item>
      <div className="mb-4">
        <FilterEditor
          filterOptions={filterOptions}
          filters={filters}
          setFilters={setFilters}
        />
      </div>
      {(isLoadingNotes || isPendingNotes) && notes.length > 0 && (
        <Item variant="muted" size="sm" className="mb-4">
          <ItemContent>
            <ItemTitle>Loading older notes…</ItemTitle>
          </ItemContent>
        </Item>
      )}
      {isShardNotesError && (
        <Item variant="outline" size="sm" className="mb-4 border-destructive">
          <ItemContent>
            <ItemTitle>
              {failedShardCount === noteListShards.length
                ? 'Could not load notes'
                : 'Some note ranges failed to load — results may be incomplete'}
            </ItemTitle>
          </ItemContent>
        </Item>
      )}
      <DataTable
        columns={columns}
        data={filteredNotes}
        virtualizeThreshold={50}
        onRowClick={(note, e) => {
          if (isOfType(note.subject, 'Patient')) {
            const patientId = resolveId(note.subject)
            navigateFromClick(`/Patient/${patientId}/visit-notes/${note.id}`, e)
          } else {
            navigateFromClick(`/notes/${note.id}`, e)
          }
        }}
      />
    </PageLayout>
  )
}
